import React from 'react';
import { ImageSquare, UploadSimple, X } from '@phosphor-icons/react';

import { Icon } from '../../components/Icon';
import { cn } from '../../lib/cn';

// Same inline error line the auth and modal-form patterns use. Imported rather
// than rebuilt for the reason stated in `modal-form/index.jsx`: two forms on one
// route must not disagree about what "wrong" looks like, and an uploader is
// almost always sitting inside a modal form next to those very fields.
import { AuthError } from '../auth';

import './upload.css';

/**
 * The image uploader. TWO surfaces share it.
 *
 *   EditStoreInfoForm (store logo, single)   EditListingModal (photos, multiple)
 *
 * Both were a bare `<input type="file">` rendered by `ModalFile`, which is to
 * say a browser-drawn "Choose file" button with no preview, no stated size, no
 * stated formats and no way to remove what you had just picked. `ModalFile` is
 * left in place for the non-image pickers; this is the image case.
 *
 * WHAT THIS DOES NOT DO, AND MUST NEVER DO
 * ----------------------------------------
 * It does not touch the request. It hands the caller a plain
 * `{ target: { files } }` and the caller keeps its own FormData keys, its own
 * field names, its own count validation and its own endpoint. That shape is
 * deliberate: both existing handlers already read `e.target.files`
 * (`Array.from(...)` in the listing modal, `?.[0]` in the store form), so the
 * drop path and the browse path arrive as the same thing and neither call site
 * had to change how it reads a file. `files` is a real `FileList` on the browse
 * path and a plain `File[]` on the drop path; every consumer treats both alike.
 *
 * THE ONE BEHAVIOURAL ADDITION
 * ----------------------------
 * Non-images are rejected here and SAID OUT LOUD. A drop zone accepts anything
 * the OS will let you drag, so a dropped PDF has to be answered; silently
 * discarding it is the one outcome a drop zone must never produce. `accept` is
 * unchanged (`image/*` at both call sites), so the browse dialog filters exactly
 * as it did, and image files pass through byte for byte untouched.
 *
 * WHY THE ZONE IS A ROLE=BUTTON DIV AND NOT A <button>
 * ----------------------------------------------------
 * The previews carry their own remove buttons, and a button inside a button is
 * invalid and unreachable. So the zone is a focusable div with the button role,
 * Enter/Space wired by hand, and the thumbs sit outside it. Drag-and-drop is a
 * pointer-only affordance; the keyboard path is the whole reason the zone is
 * focusable at all.
 *
 * The real `<input type="file">` stays in the DOM, visually hidden and out of
 * the tab order (`tabIndex={-1}`, `aria-hidden`), because the zone is its
 * proxy. It is not `display: none`: a display-none input is still clickable in
 * every browser we support, but keeping it laid out costs nothing and avoids
 * relying on that.
 */

/* Track-neutral by construction: no `ds-` or `dsk-` component is mounted here,
   only `dsa-` scaffolding over the shared tokens, so there is no track to pick
   and no `useViewport()` branch. The one thing that genuinely differs between a
   phone and a desktop is the prompt copy — "drag" means nothing on a touch
   screen — and that is presentation, so it is a CSS `(hover: hover) and
   (pointer: fine)` swap, exactly as `useViewport`'s own docblock says to prefer
   when only presentation changes. */

const isImage = (file) => typeof file?.type === 'string' && file.type.startsWith('image/');

/** One removable preview. The remove control is a real button, always visible. */
function Preview({ src, alt, caption, onRemove, removeLabel }) {
  return (
    <li className="dsa-upl__thumb">
      <img src={src} alt={alt} className="dsa-upl__thumb-img" />
      {onRemove ? (
        <button
          type="button"
          className="dsa-upl__thumb-remove"
          aria-label={removeLabel}
          onClick={onRemove}
        >
          <Icon as={X} size="sm" />
        </button>
      ) : null}
      {caption ? <span className="dsa-upl__thumb-caption">{caption}</span> : null}
    </li>
  );
}

/**
 * @param {object}   props
 * @param {string}   props.recommended  The recommended size, stated in the UI.
 *                                      Not a tooltip and not a `title`: a size
 *                                      you only find by hovering is a size
 *                                      nobody on a phone ever finds.
 * @param {string}   props.formats      Which formats, in words, beside it.
 * @param {Array}    props.items        Previews: `{ key, url, alt, caption,
 *                                      onRemove, removeLabel }`.
 * @param {Function} props.onFilesSelected  Receives `{ target: { files } }`.
 */
export function ImageUpload({
  id,
  // The surrounding `ModalField` renders `<label for={id}>`, and a `for` never
  // associates with a div, so the zone would otherwise be an unnamed button to
  // a screen reader. The caller repeats its field label here and the zone names
  // itself. Cheaper than teaching `ModalField` about non-input controls.
  label,
  accept = 'image/*',
  multiple = false,
  disabled = false,
  recommended,
  formats = 'JPG, PNG or WebP',
  note,
  items = [],
  onFilesSelected,
  // `error` arrives as a boolean from the surrounding `ModalField`, which clones
  // it onto every child. Same trap `ModalFile` documents: forwarded raw it lands
  // on the DOM as a non-boolean attribute, so it is caught here and turned into
  // the state class below rather than passed on.
  error = false,
  'aria-describedby': describedBy,
  className,
  ...rest
}) {
  const inputRef = React.useRef(null);
  const [dragging, setDragging] = React.useState(false);
  const [rejected, setRejected] = React.useState(null);

  // `dragenter` and `dragleave` fire for every DESCENDANT the pointer crosses,
  // so a zone with an icon and two lines of text flickers between states as the
  // file passes over them. Counting enters against leaves is the standard fix;
  // a boolean is not enough.
  const depth = React.useRef(0);

  const open = () => {
    if (disabled) return;
    inputRef.current?.click();
  };

  /* One funnel for both paths, so a dropped file and a browsed file are subject
     to exactly the same rule. */
  const accepted = (files) => {
    const all = Array.from(files || []);
    const good = all.filter(isImage);
    const bad = all.filter((file) => !isImage(file));

    if (bad.length) {
      setRejected(
        bad.length === 1
          ? `“${bad[0].name}” is not an image. ${formats} only.`
          : `${bad.length} files were not images and were left out. ${formats} only.`,
      );
    } else {
      setRejected(null);
    }

    return good;
  };

  const handleInputChange = (event) => {
    const good = accepted(event.target.files);
    if (good.length) onFilesSelected?.({ target: { files: good } });

    // Reset so re-picking the SAME file fires `change` again. Without this,
    // removing a logo and re-choosing the identical file is a dead click.
    event.target.value = '';
  };

  const handleDrop = (event) => {
    event.preventDefault();
    depth.current = 0;
    setDragging(false);
    if (disabled) return;

    const good = accepted(event.dataTransfer?.files);
    if (good.length) onFilesSelected?.({ target: { files: good } });
  };

  const handleDragEnter = (event) => {
    event.preventDefault();
    if (disabled) return;
    depth.current += 1;
    setDragging(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    depth.current = Math.max(0, depth.current - 1);
    if (depth.current === 0) setDragging(false);
  };

  // `dragover` must be cancelled or the browser navigates to the dropped file,
  // which loses the form and everything typed into it.
  const handleDragOver = (event) => {
    event.preventDefault();
    if (!disabled) event.dataTransfer.dropEffect = 'copy';
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
      open();
    }
  };

  const noun = multiple ? 'images' : 'an image';
  const replacing = !multiple && items.length > 0;

  return (
    <div className={cn('dsa-upl', className)} {...rest}>
      {/* NO `aria-invalid` ON THE ZONE. It is not a state `role="button"`
          supports, so it was dropped on the floor rather than announced. What
          reaches the user instead is the field's own error line: the
          surrounding `AuthField` renders it and points `aria-describedby` at
          it, and describedby IS supported here. `is-error` is the same fact
          for the eye. */}
      <div
        id={id}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={label || undefined}
        aria-disabled={disabled || undefined}
        aria-describedby={describedBy}
        className={cn(
          'dsa-upl__zone',
          dragging && 'is-dragging',
          disabled && 'is-disabled',
          error && 'is-error',
        )}
        onClick={open}
        onKeyDown={handleKeyDown}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <span className="dsa-upl__glyph" aria-hidden="true">
          <Icon as={dragging ? ImageSquare : UploadSimple} size="md" />
        </span>

        <span className="dsa-upl__prompt">
          {dragging ? (
            <span className="dsa-upl__prompt-line">Drop to upload</span>
          ) : (
            <>
              {/* Both lines are always rendered; CSS shows the one that is true
                  of the pointer in the user's hand. */}
              <span className="dsa-upl__prompt-line dsa-upl__prompt-line--drag">
                {replacing
                  ? 'Drag a new image here, or click to replace'
                  : `Drag ${noun} here, or click to browse`}
              </span>
              <span className="dsa-upl__prompt-line dsa-upl__prompt-line--tap">
                {replacing ? 'Tap to replace' : `Tap to choose ${noun}`}
              </span>
            </>
          )}
        </span>

        {/* The stated size, in the zone itself. */}
        <span className="dsa-upl__specs">
          {formats}
          {recommended ? ` · Recommended ${recommended}` : null}
        </span>

        <input
          ref={inputRef}
          type="file"
          className="dsa-upl__input"
          accept={accept}
          multiple={multiple || undefined}
          disabled={disabled}
          tabIndex={-1}
          aria-hidden="true"
          onChange={handleInputChange}
          /* MANDATORY. The input lives inside the zone, and `.click()` on a
             child dispatches a click that BUBBLES: without this, opening the
             picker re-triggers the zone's own onClick, which calls `.click()`
             again, and the handler recurses until the stack blows. */
          onClick={(event) => event.stopPropagation()}
        />
      </div>

      {items.length ? (
        <ul className="dsa-upl__thumbs">
          {items.map((item, index) => (
            <Preview
              key={item.key ?? item.url ?? index}
              src={item.url}
              alt={item.alt || ''}
              caption={item.caption}
              onRemove={item.onRemove}
              removeLabel={item.removeLabel || 'Remove image'}
            />
          ))}
        </ul>
      ) : null}

      {note ? <p className="dsa-upl__note">{note}</p> : null}

      <AuthError>{rejected}</AuthError>
    </div>
  );
}

export default ImageUpload;
